import { useState, type ReactNode } from 'react';
import { AlarmClock, AlarmClockOff, CalendarDays } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { SNOOZE_HOUR, SNOOZE_PRESETS, addDays, isSnoozed, snoozeDay, snoozeUntil, teamToday, wallClockTime } from '@/lib/snooze';

const hourLabel = `${String(SNOOZE_HOUR).padStart(2, '0')}:00`;

/** A Snooze button while it's snoozed. */
export const SNOOZED_BUTTON_CLASS = 'border-info-border bg-info-soft text-info-strong hover:bg-info/10';

/**
 * The Snooze menu: a few days, or a date from the calendar, and "Unsnooze now" while it's
 * snoozed. Used by inbox Conversations (T-48) and tasks (T-50). `open` is owned by the caller
 * (the inbox's b shortcut opens it), and so is the button, passed as `trigger`.
 */
export function SnoozeMenu({
  snoozedUntil,
  open,
  onOpenChange,
  onSnooze,
  trigger,
  backLabel = 'Back in the lists',
  align = 'start',
}: {
  snoozedUntil: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** A time to come back, or null to wake it now. */
  onSnooze: (until: Date | null) => void;
  trigger: ReactNode;
  /** Heads the menu: "{backLabel} at 08:00 on". */
  backLabel?: string;
  align?: 'start' | 'end';
}) {
  const [picking, setPicking] = useState(false);
  const snoozed = isSnoozed(snoozedUntil);
  return (
    <>
      <DropdownMenu open={open} onOpenChange={onOpenChange}>
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
        <DropdownMenuContent align={align} className="w-60">
          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
            {backLabel} at {hourLabel} on
          </DropdownMenuLabel>
          {SNOOZE_PRESETS.map((preset) => {
            const until = snoozeUntil(preset.days);
            return (
              <DropdownMenuItem key={preset.days} onSelect={() => onSnooze(until)}>
                {preset.label}
                <span className="ml-auto text-xs text-muted-foreground">{snoozeDay(until)}</span>
              </DropdownMenuItem>
            );
          })}
          <DropdownMenuItem onSelect={() => setPicking(true)}>
            <CalendarDays className="text-muted-foreground" />
            Pick a date…
          </DropdownMenuItem>
          {snoozed ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => onSnooze(null)}>
                <AlarmClockOff className="text-muted-foreground" />
                Unsnooze now
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      <SnoozeDateDialog
        open={picking}
        onOpenChange={setPicking}
        onPick={(until) => {
          setPicking(false);
          onSnooze(until);
        }}
      />
    </>
  );
}

/** A date from the calendar, from tomorrow on; it comes back at SNOOZE_HOUR that day. */
function SnoozeDateDialog({ open, onOpenChange, onPick }: { open: boolean; onOpenChange: (open: boolean) => void; onPick: (until: Date) => void }) {
  const [day, setDay] = useState<Date | undefined>();
  const today = teamToday();
  const tomorrow = addDays(today, 1);
  const lastDay = addDays(today, 365);
  // The calendar works in this browser's dates; the day is then taken as the team's.
  const local = (d: { year: number; month: number; day: number }) => new Date(d.year, d.month - 1, d.day);
  const until = day ? wallClockTime({ year: day.getFullYear(), month: day.getMonth() + 1, day: day.getDate() }, SNOOZE_HOUR) : null;
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setDay(undefined);
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Snooze until</DialogTitle>
          <DialogDescription>{until ? `It comes back at ${hourLabel} on ${snoozeDay(until)}.` : `Pick the day it should come back (at ${hourLabel}).`}</DialogDescription>
        </DialogHeader>
        <Calendar
          mode="single"
          selected={day}
          onSelect={setDay}
          defaultMonth={local(tomorrow)}
          startMonth={local(tomorrow)}
          endMonth={local(lastDay)}
          disabled={{ before: local(tomorrow), after: local(lastDay) }}
          className="mx-auto rounded-md border"
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!until} onClick={() => until && onPick(until)}>
            <AlarmClock />
            Snooze
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
